import {
  EMBRYO_GRADES,
  createEmptyCycle,
  createEmptyEmbryoEntry,
  type Cycle,
  type OocyteCountMetric,
  type OocyteCohortDisposition,
  type ParsedNotesData,
  type SavedSpecimenRef,
} from "../components/frozen-storage-data";

/**
 * Turning what was saved back into the form she sees.
 *
 * This lived inside `AddSpecimenModal`, unexported, in five thousand lines of
 * JSX — so the one step that decides whether a reopened intake is faithful was
 * the one step no test could reach. It is the reason a browser run was needed
 * to discover that the freezing method and the sample's source still came back
 * blank. It lives here so that question can be asked in a test instead.
 */

export type SpecimenPrefill = {
  type?: "eggs" | "embryos" | "sperm";
  /**
   * The counts she explicitly marked unknown, as keys rather than nulls.
   *
   * A blank form carries the same nulls as a deliberate clear, so the values
   * alone cannot say which happened — and guessing wrong erases figures she
   * recorded, which is what `bfde19e30` had to revert. This is the signal that
   * reaches `buildFertilityMetrics`, where it becomes `value: null` against the
   * existing row's id.
   */
  clearedCounts?: OocyteCountMetric[];
  /**
   * The saved rows this prefill was read from, in the order the intake writes
   * them.
   *
   * The intake is an *add* form and "Modifier le résultat" opens it, so
   * reopening a faithfully prefilled form and pressing save wrote a second
   * cohort: eight frozen eggs became sixteen. Nothing linked the form back to
   * the rows that filled it — `useCreateSpecimen` decides create-vs-update on
   * `cycle.specimenIds[i]`, and only an in-session save ever seeded that.
   *
   * The order matters as much as the ids: the write loop labels maturity by
   * position, so these arrive bucketed the same way (see
   * `buildIntakePrefillFromSpecimens`).
   */
  specimenIds?: string[];
  /** Which of `specimenIds` a reduction may retire, and the placement to close. */
  specimenRefs?: Record<string, SavedSpecimenRef>;
  /**
   * The whole form as it was saved, read off the specimen's own notes.
   *
   * Applied before every other field here, so the reopened form starts true and
   * the explicit ones — which come from the cycle's steps and the specimen's
   * columns — refine it. Without it the intake reopened blank in every field it
   * had never learned to read, and once it may save an existing row, blank is
   * what it writes back.
   */
  savedNotes?: ParsedNotesData;
  /**
   * How many rows this cycle holds, spent ones included.
   *
   * `specimenIds` lists only the rows the intake may act on, so it cannot tell
   * "no cohort was ever saved" from "the whole cohort has been thawed".
   */
  savedRowTotal?: number;
  clinicName?: string;
  retrievalDate?: string;
  freezeDate?: string;
  collectedAt?: string;
  totalRetrieved?: number;
  matureMII?: number;
  immatureMI?: number;
  degenerated?: number;
  numberFrozen?: number;
  numberFertilized?: number;
  cohortDisposition?: OocyteCohortDisposition;
  fertilizationMethod?: string;
  /**
   * Her embryos' grades from the cycle's embryo-development step, one per
   * graded embryo, in the order she added them.
   */
  embryoGrades?: readonly string[];
  embryoDayOfDevelopment?: string;
  embryoPgtPerformed?: string;
  totalEmbryosCreated?: number;
  numberTested?: number;
  numberEuploid?: number;
  numberAneuploid?: number;
  numberMosaic?: number;
  numberNoResult?: number;
  storageLocation?: string;
  // Where it is stored, read back from the saved specimen's active storage
  // location. Only `storageLocation` used to come back, and from the
  // fertilization step rather than the specimen — so the whole storage block
  // reopened blank however often she filled it in.
  storageState?: "stored";
  storageCity?: string;
  storageCountry?: string;
  storageCountryName?: string;
  storageLocationType?: string;
  // Sperm-specific prefill (from the treatment hub's sperm_collection +
  // analysis steps).
  abstinencePeriod?: number;
  concentration?: number;
  totalCount?: number;
  motilityPercent?: number;
  morphologyPercent?: number;
  volumeMl?: number;
};

export function createCycleWithCountry(country: string): Cycle {
  const cycle = createEmptyCycle();
  cycle.country = country;
  return cycle;
}

/**
 * Everything the saved notes remember, laid down before anything else.
 *
 * These are the form's own values, serialised by `buildNotesString` on the save
 * that created the row. The explicit prefill fields then overwrite what they
 * know, because they read the specimen's columns and the cycle's steps, which
 * are the record rather than a copy of the form.
 */
export function applySavedNotes(c: Cycle, saved: ParsedNotesData): void {
  if (saved.specimenSource) c.source = saved.specimenSource;
  if (saved.clinicName) c.clinicName = saved.clinicName;
  if (saved.country) c.country = saved.country;
  if (saved.storageCity) c.storageCity = saved.storageCity;
  if (saved.storageLocationType) {
    c.storageLocationType = saved.storageLocationType;
  }
  if (saved.storageId) c.storageId = saved.storageId;
  if (saved.storageState) c.storageState = saved.storageState;
  if (saved.freeTextNotes) c.notes = saved.freeTextNotes;

  const o = c.oocyte;
  if (saved.retrievalDate) o.retrievalDate = saved.retrievalDate;
  if (saved.freezingMethod) o.freezingMethod = saved.freezingMethod;
  if (saved.totalRetrieved != null) o.totalRetrieved = saved.totalRetrieved;
  if (saved.matureMII != null) o.matureMII = saved.matureMII;
  if (saved.immatureMI != null) o.immatureMI = saved.immatureMI;
  if (saved.degenerated != null) o.degenerated = saved.degenerated;
  if (saved.cohortDisposition) o.cohortDisposition = saved.cohortDisposition;
  if (saved.numberFertilized != null) {
    o.numberFertilized = saved.numberFertilized;
  }
  if (saved.numberThawed != null) o.numberThawed = saved.numberThawed;
  if (saved.postThawSurvivalRate != null) {
    o.postThawSurvivalRate = saved.postThawSurvivalRate;
  }

  const e = c.embryo;
  if (saved.fertilisationDate) e.fertilisationDate = saved.fertilisationDate;
  if (saved.fertilisationMethod) {
    e.fertilisationMethod = saved.fertilisationMethod;
  }
  if (saved.dayOfDevelopment) e.dayOfDevelopment = saved.dayOfDevelopment;
  if (saved.pgtPerformed) e.pgtPerformed = saved.pgtPerformed;
  if (saved.totalEmbryosCreated != null) {
    e.totalEmbryosCreated = saved.totalEmbryosCreated;
  }
  if (saved.numberTested != null) e.numberTested = saved.numberTested;
  if (saved.numberEuploid != null) e.numberEuploid = saved.numberEuploid;
  if (saved.numberAneuploid != null) e.numberAneuploid = saved.numberAneuploid;
  if (saved.numberMosaic != null) e.numberMosaic = saved.numberMosaic;
  if (saved.numberNoResult != null) e.numberNoResult = saved.numberNoResult;

  /*
   * Her embryos, one by one.
   *
   * Dropping these was the worst thing on this branch. `buildPrefillCycle`
   * starts from `createEmptyCycle`, whose mode is "group", so an individual
   * cohort reopened as a group one with no entries — and because the intake now
   * writes the whole record back, saving would have replaced every embryo id,
   * grade, genetic status and sex with a stub reading
   * `"Day of Development: \nNumber Frozen: 0"`. Permanently.
   */
  if (saved.individualEmbryos?.length) {
    c.embryoEntryMode = "individual";
    e.individualEntries = saved.individualEmbryos.map((entry) => ({
      ...createEmptyEmbryoEntry(),
      embryoId: entry.embryoId ?? "",
      dayOfDevelopment: entry.dayOfDevelopment ?? "",
      grade: entry.grade ?? "",
      geneticStatus: entry.geneticStatus ?? "",
      sex: entry.sex ?? "",
      status: entry.status ?? "",
      notes: entry.notes ?? "",
    }));
  }

  const sp = c.sperm;
  if (saved.collectionDate) sp.collectedAt = saved.collectionDate;
  if (saved.collectionMethod) sp.collectionMethod = saved.collectionMethod;
  if (saved.processingMethod) sp.processingMethod = saved.processingMethod;
  if (saved.abstinencePeriod != null) {
    sp.abstinencePeriod = saved.abstinencePeriod;
  }
  if (saved.concentration != null) sp.concentration = saved.concentration;
  if (saved.totalCount != null) sp.totalCount = saved.totalCount;
  if (saved.motilityPercent != null) sp.motilityPercent = saved.motilityPercent;
  if (saved.morphologyPercent != null) {
    sp.morphologyPercent = saved.morphologyPercent;
  }
  if (saved.volumeMl != null) sp.volumeMl = saved.volumeMl;
  if (saved.dnaFragmentationPercent != null) {
    sp.dnaFragmentationPercent = saved.dnaFragmentationPercent;
  }
  if (saved.vitalityPercent != null) sp.vitalityPercent = saved.vitalityPercent;
  if (saved.progressiveMotilityPercent != null) {
    sp.progressiveMotilityPercent = saved.progressiveMotilityPercent;
  }
  if (saved.otherDetails) sp.otherDetails = saved.otherDetails;

  if (saved.freezeDate) {
    o.freezeDate = saved.freezeDate;
    e.freezeDate = saved.freezeDate;
    sp.freezeDate = saved.freezeDate;
  }
  if (saved.numberFrozen != null) {
    o.numberFrozen = saved.numberFrozen;
    e.numberFrozen = saved.numberFrozen;
    sp.numberFrozen = saved.numberFrozen;
  }
}

/**
 * Her embryos' grades, as individual embryos.
 *
 * A grade belongs to one embryo, and only the individual entries carry one, so
 * this is the only place grades can land. Placed only on a first intake: a
 * saved cohort's mode and entries come from its own notes, and a save writes
 * the whole record back. And only when every graded embryo can be one of the
 * frozen ones; the rest of the frozen count stays as blank entries for her to
 * fill in. A grade the intake does not offer is left blank rather than saved
 * behind a dropdown that shows nothing.
 */
function applyEmbryoGrades(c: Cycle, prefill: SpecimenPrefill): void {
  const grades = prefill.embryoGrades ?? [];
  const frozen = c.embryo.numberFrozen;
  if (
    grades.length === 0 ||
    prefill.specimenIds?.length ||
    c.embryo.individualEntries.length > 0 ||
    frozen < grades.length
  ) {
    return;
  }
  c.embryoEntryMode = "individual";
  c.embryo.individualEntries = Array.from({ length: frozen }, (_, index) => {
    const grade = grades[index];
    return {
      ...createEmptyEmbryoEntry(),
      grade:
        grade && EMBRYO_GRADES.some((option) => option.id === grade)
          ? grade
          : "",
    };
  });
}

export function buildPrefillCycle(
  prefill: SpecimenPrefill,
  country = "US",
): Cycle {
  const c = createCycleWithCountry(country);
  if (prefill.savedNotes) applySavedNotes(c, prefill.savedNotes);
  /*
   * What she marked unknown, applied AFTER the saved notes and clearing their
   * value with it.
   *
   * The notes are the form's copy, written when the row was saved; the metric
   * row is the record, and it is what `clearedOocyteCounts` reads. A count she
   * cleared afterwards is gone from the row but still sitting in the older
   * notes — so without this the form reopened showing "I don't know" ticked
   * next to the very figure she had taken back, and un-ticking would have
   * written that figure straight back in.
   */
  if (prefill.clearedCounts?.length) {
    c.clearedOocyteCounts = [...prefill.clearedCounts];
    for (const metric of prefill.clearedCounts) c.oocyte[metric] = null;
  }
  // The rows the form is being filled from, so a save updates them instead of
  // adding a second cohort beside them. Stamped with the type they were read
  // for: two callers change a cycle's type afterwards and keep its ids.
  if (prefill.specimenIds?.length) {
    c.specimenIds = [...prefill.specimenIds];
    c.specimenIdsType = prefill.type ?? "eggs";
  }
  if (prefill.specimenRefs) c.specimenRefs = { ...prefill.specimenRefs };
  if (prefill.type) c.type = prefill.type;
  if (prefill.clinicName) c.clinicName = prefill.clinicName;
  if (prefill.storageLocation) c.storageId = prefill.storageLocation;
  if (prefill.storageState) c.storageState = prefill.storageState;
  // Overwrites the device default rather than falling back to it: a saved
  // placement's own country is truer than where she happens to be standing.
  if (prefill.storageCountry) c.country = prefill.storageCountry;
  if (prefill.storageCity) c.storageCity = prefill.storageCity;
  if (prefill.storageCountryName) {
    c.storageCountryName = prefill.storageCountryName;
  }
  if (prefill.storageLocationType) {
    c.storageLocationType = prefill.storageLocationType;
  }

  if (prefill.type === "embryos") {
    if (prefill.freezeDate) c.embryo.freezeDate = prefill.freezeDate;
    if (prefill.numberFrozen != null) {
      c.embryo.numberFrozen = prefill.numberFrozen;
    }
    if (prefill.totalEmbryosCreated != null) {
      c.embryo.totalEmbryosCreated = prefill.totalEmbryosCreated;
    }
    if (prefill.embryoDayOfDevelopment) {
      c.embryo.dayOfDevelopment = prefill.embryoDayOfDevelopment;
    }
    if (prefill.embryoPgtPerformed) {
      c.embryo.pgtPerformed = prefill.embryoPgtPerformed;
    }
    if (prefill.fertilizationMethod) {
      c.embryo.fertilisationMethod = prefill.fertilizationMethod;
    }
    if (prefill.numberTested != null) {
      c.embryo.numberTested = prefill.numberTested;
    }
    if (prefill.numberEuploid != null) {
      c.embryo.numberEuploid = prefill.numberEuploid;
    }
    if (prefill.numberAneuploid != null) {
      c.embryo.numberAneuploid = prefill.numberAneuploid;
    }
    if (prefill.numberMosaic != null) {
      c.embryo.numberMosaic = prefill.numberMosaic;
    }
    if (prefill.numberNoResult != null) {
      c.embryo.numberNoResult = prefill.numberNoResult;
    }
    if (prefill.retrievalDate) {
      c.embryo.fertilisationDate = prefill.retrievalDate;
    }
    applyEmbryoGrades(c, prefill);
  } else if (prefill.type === "sperm") {
    if (prefill.collectedAt) c.sperm.collectedAt = prefill.collectedAt;
    if (prefill.freezeDate) c.sperm.freezeDate = prefill.freezeDate;
    if (prefill.numberFrozen != null) {
      c.sperm.numberFrozen = prefill.numberFrozen;
    }
    if (prefill.abstinencePeriod != null) {
      c.sperm.abstinencePeriod = prefill.abstinencePeriod;
    }
    if (prefill.concentration != null) {
      c.sperm.concentration = prefill.concentration;
    }
    if (prefill.totalCount != null) c.sperm.totalCount = prefill.totalCount;
    if (prefill.motilityPercent != null) {
      c.sperm.motilityPercent = prefill.motilityPercent;
    }
    if (prefill.morphologyPercent != null) {
      c.sperm.morphologyPercent = prefill.morphologyPercent;
    }
    if (prefill.volumeMl != null) c.sperm.volumeMl = prefill.volumeMl;
  } else {
    if (prefill.retrievalDate) c.oocyte.retrievalDate = prefill.retrievalDate;
    if (prefill.freezeDate) c.oocyte.freezeDate = prefill.freezeDate;
    if (prefill.totalRetrieved != null) {
      c.oocyte.totalRetrieved = prefill.totalRetrieved;
    }
    if (prefill.matureMII != null) c.oocyte.matureMII = prefill.matureMII;
    if (prefill.immatureMI != null) c.oocyte.immatureMI = prefill.immatureMI;
    if (prefill.degenerated != null) {
      c.oocyte.degenerated = prefill.degenerated;
    }
    if (prefill.numberFrozen != null) {
      c.oocyte.numberFrozen = prefill.numberFrozen;
    }
    if (prefill.numberFertilized != null) {
      c.oocyte.numberFertilized = prefill.numberFertilized;
    }
    if (prefill.cohortDisposition) {
      c.oocyte.cohortDisposition = prefill.cohortDisposition;
    }
  }
  return c;
}
